import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock } from "lucide-react";
import { toast } from "sonner";
import { getBlog } from "../lib/api";
import { Reveal } from "../components/Primitives";

export default function Journal() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getBlog()
      .then(setPosts)
      .catch(() => toast.error("Could not load journal."))
      .finally(() => setLoading(false));
  }, []);

  const [featured, ...rest] = posts;

  return (
    <div data-testid="journal-page">
      <section className="pt-36 pb-12 md:pt-44 md:pb-16 bg-[#120726] grain text-center">
        <div className="container-loft max-w-3xl mx-auto">
          <Reveal>
            <p className="overline mb-5">The Journal</p>
            <h1 className="font-serif text-5xl md:text-7xl font-light leading-[1.03] text-[#EFE7FA]">
              Reflections from <span className="text-gradient">the Loft</span>
            </h1>
            <p className="mt-7 text-lg md:text-xl font-light leading-relaxed text-[#EFE7FA]/75">
              Gentle wisdom on healing, strategy, and intentional living.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-28 bg-[#1a0d33] pt-12">
        <div className="container-loft">
          {loading ? (
            <div className="space-y-8">
              <div className="card-soft h-80 animate-pulse" />
            </div>
          ) : (
            <>
              {featured && (
                <Reveal>
                  <Link to={`/journal/${featured.slug}`} data-testid="journal-featured" className="card-soft grid md:grid-cols-2 overflow-hidden mb-14 group">
                    <div className="h-72 md:h-auto overflow-hidden">
                      <img src={featured.cover} alt={featured.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    </div>
                    <div className="p-8 md:p-12 flex flex-col justify-center">
                      <div className="flex items-center gap-4 mb-4">
                        <span className="overline text-[#4FD6AD]">{featured.category}</span>
                        <span className="flex items-center gap-1 text-xs text-[#EFE7FA]/50"><Clock size={13} /> {featured.read_time}</span>
                      </div>
                      <h2 className="font-serif text-3xl md:text-4xl font-light text-[#EFE7FA] leading-tight">{featured.title}</h2>
                      <p className="mt-4 text-[#EFE7FA]/70 font-light leading-relaxed">{featured.excerpt}</p>
                      <span className="mt-6 inline-flex items-center gap-2 text-[#C4A2E8] font-normal group-hover:gap-3 transition-all">
                        Read article <ArrowRight size={16} />
                      </span>
                    </div>
                  </Link>
                </Reveal>
              )}

              <div className="grid md:grid-cols-3 gap-8">
                {rest.map((p, i) => (
                  <Reveal key={p.slug} delay={i * 0.08}>
                    <Link to={`/journal/${p.slug}`} data-testid={`journal-card-${p.slug}`} className="card-soft overflow-hidden h-full flex flex-col group">
                      <div className="h-52 overflow-hidden">
                        <img src={p.cover} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                      </div>
                      <div className="p-6 flex flex-col flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <span className="overline text-[#4FD6AD]">{p.category}</span>
                          <span className="flex items-center gap-1 text-xs text-[#EFE7FA]/50"><Clock size={12} /> {p.read_time}</span>
                        </div>
                        <h3 className="font-serif text-2xl font-light text-[#EFE7FA] leading-snug">{p.title}</h3>
                        <p className="mt-3 text-sm font-light leading-relaxed text-[#EFE7FA]/70 flex-1">{p.excerpt}</p>
                        <span className="mt-5 inline-flex items-center gap-2 text-sm text-[#C4A2E8] group-hover:gap-3 transition-all">
                          Read more <ArrowRight size={15} />
                        </span>
                      </div>
                    </Link>
                  </Reveal>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
